import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const PUBLIC_HOST = "https://2ra-edition.com";

// Paths that are user-specific / never should be indexed
const NOINDEX_PATHS = [
  "/login",
  "/reset-password",
  "/profile",
  "/upload",
  "/my-submissions",
  "/referral",
  "/admin",
  "/research/shared",
];

const SITE_DEFAULT_DESC =
  "The Archive by 2RA-Edition — a curated digital library of 8,000+ books, academic articles, research reports, and theses in engineering, extractive metallurgy, mining, chemistry, and scientific research.";

const PAGE_META = {
  "/": {
    title: "The Archive — Digital Library of Engineering & Scientific Research",
    description: SITE_DEFAULT_DESC,
  },
  "/search": {
    title: "Search the Archive — 2RA-Edition Digital Library",
    description:
      "Search 8,000+ books, articles, theses, and research reports across engineering, extractive metallurgy, mining, hydrometallurgy, and applied science. Full-text search inside every PDF.",
  },
  "/smart-search": {
    title: "Smart Search — Advanced Faceted Filters | The Archive",
    description:
      "Combine full-text search with filters by language, category, year, author, organization, and tags. Ideal for research in extractive metallurgy, mining engineering, and industrial chemistry.",
  },
  "/collections": {
    title: "Reading Collections | The Archive",
    description:
      "Build public or private reading lists from documents in The Archive — group papers by research topic, project, course, or client, and share them with the community.",
  },
  "/topics": {
    title: "Browse Research Topics | The Archive",
    description:
      "Explore The Archive's most active research topics — engineering, extractive metallurgy, mining, hydrometallurgy, industrial chemistry, and applied science. Each topic gathers the library's books, articles, theses, and reports on that subject.",
  },
  "/subscriptions": {
    title: "Subscription Plans & Pricing | The Archive",
    description:
      "Free 5-day trial with unlimited downloads. Continue on the free tier (10 downloads/month) or subscribe monthly, quarterly, semi-annually, or annually. Full access to 8,000+ documents.",
  },
  "/about": {
    title: "About The Archive — 2RA-Edition",
    description:
      "Learn about The Archive, an independent digital library based in the DRC, providing engineers, researchers, and students worldwide with technical literature in extractive metallurgy, mining, and applied science.",
  },
  "/terms": {
    title: "Terms of Service | The Archive",
    description:
      "Terms of Service for The Archive by 2RA-Edition. Account, subscription, refund, submission, licensing, privacy, and governing-law provisions.",
  },
};

function setOrCreateMeta(name, attr, value) {
  let el = document.querySelector(`meta[${name}]`);
  if (!el) {
    el = document.createElement("meta");
    document.head.appendChild(el);
    const [k, v] = name.split("=");
    el.setAttribute(k, v.replace(/"/g, ""));
  }
  el.setAttribute(attr, value);
}

function setCanonical(url) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", url);
}

function setRobotsMeta(shouldIndex) {
  let m = document.querySelector('meta[name="robots"]');
  if (!m) {
    m = document.createElement("meta");
    m.setAttribute("name", "robots");
    document.head.appendChild(m);
  }
  m.setAttribute("content", shouldIndex ? "index, follow" : "noindex, nofollow");
}

/**
 * SeoHead — on every route change, updates:
 *  - <link rel="canonical"> to the current pathname (fixes SPA canonical bug)
 *  - <meta name="robots"> to noindex for user-specific pages
 *  - document.title + <meta name="description"> to per-page copy
 *  - og:url + og:title + og:description to match
 *
 * Item detail pages (`/items/:id`) handle their own SEO tags via ItemDetailPage.
 */
const SeoHead = () => {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname;

    // Don't touch item detail pages — they manage their own SEO
    if (path.startsWith("/items/")) return;

    const isIndexable = !NOINDEX_PATHS.some((p) => path === p || path.startsWith(p + "/"));
    setRobotsMeta(isIndexable);

    // Canonical: strip trailing slash for consistency, keep the root "/" as-is
    const normalizedPath = path === "/" ? "/" : path.replace(/\/$/, "");
    const canonicalUrl = `${PUBLIC_HOST}${normalizedPath}`;
    setCanonical(canonicalUrl);

    const meta = PAGE_META[normalizedPath] || {
      title: "The Archive — 2RA-Edition",
      description: SITE_DEFAULT_DESC,
    };
    document.title = meta.title;
    setOrCreateMeta('name="description"', "content", meta.description);
    setOrCreateMeta('property="og:url"', "content", canonicalUrl);
    setOrCreateMeta('property="og:title"', "content", meta.title);
    setOrCreateMeta('property="og:description"', "content", meta.description);
    setOrCreateMeta('property="og:type"', "content", "website");
  }, [location.pathname]);

  return null;
};

export default SeoHead;
